import { ErrorCode, Presence } from '@infiltre/shared';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { selectRoom } from '@/entities/room';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { DriverStatusKind } from '@/shared/lib/driver';
import { type UiEventMap, useUiEvent } from '@/shared/lib/events';
import { type MessageKey, useT } from '@/shared/lib/i18n';

/** App-wide toast surface for presence + driver-status nudges. HostChanged keeps its own banner. */
const TOAST_TTL_MS = 5_000;

/** Same engine error within this window collapses into one toast (debounces double-clicks). */
const ERROR_DEDUPE_WINDOW_MS = 1_500;

type Toast = {
  id: string;
  message: string;
  tone: 'info' | 'warn' | 'danger';
  at: number;
};

type StatusKey =
  | typeof DriverStatusKind.RoomNotFound
  | typeof DriverStatusKind.RoomFull
  | typeof DriverStatusKind.KickedDisconnected
  | typeof DriverStatusKind.Unauthorized;

const STATUS_TOAST: Record<StatusKey, { key: MessageKey; tone: Toast['tone'] }> = {
  [DriverStatusKind.RoomNotFound]: { key: 'online.toast.room_not_found', tone: 'danger' },
  [DriverStatusKind.RoomFull]: { key: 'online.toast.room_full', tone: 'warn' },
  [DriverStatusKind.KickedDisconnected]: {
    key: 'online.toast.kicked_after_grace',
    tone: 'warn',
  },
  [DriverStatusKind.Unauthorized]: { key: 'online.toast.unauthorized', tone: 'danger' },
};

const PRESENCE_TOAST: Record<Presence, { key: MessageKey; tone: Toast['tone'] }> = {
  [Presence.Online]: { key: 'online.toast.presence_online', tone: 'info' },
  [Presence.Reconnecting]: { key: 'online.toast.presence_reconnecting', tone: 'warn' },
  [Presence.Offline]: { key: 'online.toast.presence_offline', tone: 'warn' },
};

const SERVER_ERROR_KEY: Partial<Record<ErrorCode, MessageKey>> = {
  [ErrorCode.ActorMismatch]: 'online.toast.server_error.actor_mismatch',
  [ErrorCode.UnknownPlayer]: 'online.toast.server_error.unknown_player',
  [ErrorCode.NameRequired]: 'online.toast.server_error.name_required',
  [ErrorCode.InvalidMessage]: 'online.toast.server_error.invalid_message',
  [ErrorCode.Unauthorized]: 'online.toast.server_error.unauthorized',
  [ErrorCode.OnlyHost]: 'online.toast.server_error.only_host',
  [ErrorCode.WrongPhase]: 'online.toast.server_error.wrong_phase',
  [ErrorCode.InvalidTarget]: 'online.toast.server_error.invalid_target',
  [ErrorCode.NotYourGuess]: 'online.toast.server_error.not_your_guess',
  [ErrorCode.NotAMember]: 'online.toast.server_error.not_a_member',
  [ErrorCode.NotEnoughPlayers]: 'online.toast.server_error.not_enough_players',
  [ErrorCode.TooManyPlayers]: 'online.toast.server_error.too_many_players',
  [ErrorCode.RoomAlreadyExists]: 'online.toast.server_error.room_already_exists',
  [ErrorCode.NoSession]: 'online.toast.server_error.no_session',
  [ErrorCode.CreateViaRest]: 'online.toast.server_error.create_via_rest',
  [ErrorCode.SystemOnlyMessage]: 'online.toast.server_error.system_only_message',
  [ErrorCode.Internal]: 'online.toast.server_error.internal',
  // RoomFull / RoomNotFound surface via DriverStatusKind toasts, not here.
};

export const Toaster = () => {
  const t = useT();
  const driverStatus = useSessionStore((s) => s.driverStatus);
  const room = useGameStore(selectRoom);
  const identity = useSessionStore((s) => s.identity);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const lastStatusKindRef = useRef<string | null>(null);
  const lastErrorRef = useRef<{ code: ErrorCode; at: number } | null>(null);

  // Presence toast — fired once per emit. Self-transitions skipped.
  const onPresence = useCallback(
    (payload: UiEventMap['presence-changed']) => {
      if (!room || !identity) return;
      if (payload.playerId === identity.playerId) return;
      const player = room.players.find((p) => p.id === payload.playerId);
      if (!player) return;
      const cfg = PRESENCE_TOAST[payload.presence];
      if (!cfg) return;
      setToasts((prev) => [
        ...prev,
        {
          id: `presence-${payload.playerId}-${payload.at}`,
          message: t(cfg.key, { name: player.name }),
          tone: cfg.tone,
          at: payload.at,
        },
      ]);
    },
    [room, identity, t],
  );
  useUiEvent('presence-changed', onPresence);

  const onError = useCallback(
    (payload: UiEventMap['error']) => {
      // Dedupe burst-firing of the same code (double-click, network retry).
      const prev = lastErrorRef.current;
      if (prev && prev.code === payload.code && payload.at - prev.at < ERROR_DEDUPE_WINDOW_MS) {
        return;
      }
      lastErrorRef.current = { code: payload.code, at: payload.at };
      const mappedKey = SERVER_ERROR_KEY[payload.code];
      const message = mappedKey
        ? t(mappedKey)
        : t('online.toast.server_error.generic', { message: payload.message ?? payload.code });
      setToasts((curr) => [
        ...curr,
        {
          id: `error-${payload.at}`,
          message,
          tone: 'danger',
          at: payload.at,
        },
      ]);
    },
    [t],
  );
  useUiEvent('error', onError);

  useEffect(() => {
    if (!(driverStatus.kind in STATUS_TOAST)) {
      lastStatusKindRef.current = driverStatus.kind;
      return;
    }
    if (lastStatusKindRef.current === driverStatus.kind) return;
    lastStatusKindRef.current = driverStatus.kind;
    const cfg = STATUS_TOAST[driverStatus.kind as StatusKey];
    setToasts((prev) => [
      ...prev,
      {
        id: `status-${driverStatus.kind}-${Date.now()}`,
        message: t(cfg.key),
        tone: cfg.tone,
        at: Date.now(),
      },
    ]);
  }, [driverStatus, t]);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setToasts((prev) => prev.filter((toast) => now - toast.at < TOAST_TTL_MS));
    }, 500);
    return () => clearInterval(timer);
  }, [toasts.length]);

  if (toasts.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-3 z-50 flex flex-col items-center gap-2 px-3"
      aria-live="polite"
    >
      <AnimatePresence>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22 }}
            className={[
              'pointer-events-auto max-w-sm rounded-md border-2 px-3 py-1.5 font-bold font-display text-[12px] uppercase tracking-[0.14em] shadow-[3px_3px_0_var(--color-ink)]',
              toast.tone === 'danger'
                ? 'border-und bg-cream text-und'
                : toast.tone === 'warn'
                  ? 'border-pop bg-cream text-ink'
                  : 'border-ink bg-cream text-ink',
            ].join(' ')}
            role={toast.tone === 'danger' ? 'alert' : 'status'}
          >
            {toast.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
